import { useCallback, useRef } from "react";
import { ApiError } from "./errors";
import { newIdempotencyKey } from "./idempotency";

/**
 * One Idempotency-Key per logical submission.
 *
 * The mutator generates a fresh key per request, which is right for one-shot
 * actions but wrong for a create form: if the response is lost after the
 * server committed, pressing Submit again with a new key creates a duplicate.
 * `headersFor(slot, payload)` returns the same key while the payload for that
 * slot is unchanged, and a new key as soon as it changes (reusing a key with a
 * different payload is an `idempotency_conflict`). Call `reset()` after a
 * confirmed success so the next submission is a new operation.
 */
export function useIdempotencyKey() {
  const slots = useRef(new Map<string, { fingerprint: string; key: string }>());

  const headersFor = useCallback((slot: string, payload: unknown): Record<string, string> => {
    const fingerprint = JSON.stringify(payload ?? null);
    const current = slots.current.get(slot);
    if (current && current.fingerprint === fingerprint) return { "Idempotency-Key": current.key };
    const key = newIdempotencyKey();
    slots.current.set(slot, { fingerprint, key });
    return { "Idempotency-Key": key };
  }, []);

  const reset = useCallback(() => slots.current.clear(), []);
  /** Forget one slot (for flows that submit several independent operations). */
  const resetSlot = useCallback((slot: string) => {
    slots.current.delete(slot);
  }, []);

  return { headersFor, reset, resetSlot };
}

/**
 * True when the server definitively refused the operation (a 4xx other than a
 * rate-limit 429), so a retry is a new operation and should get a new key.
 * Network errors, 5xx and rate_limited 429 are ambiguous: the operation may
 * have committed, keep the key.
 */
export function isDefinitiveRejection(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status < 400 || error.status >= 500) return false;
  // The backend also answers quota_exceeded with 429: a refusal of this very
  // operation (a limit check), not throttling, so it did not happen.
  if (error.status === 429) return error.code === "quota_exceeded";
  // "An identical idempotent request is still in progress": the first attempt
  // may still commit. Keep the key so a retry replays it instead of creating a
  // duplicate. (idempotency_conflict — key reused with another payload — is
  // definitive: that key can't be used again.)
  const reason = (error.details as { reason?: unknown } | undefined)?.reason;
  return reason !== "idempotency_in_progress";
}
